/**
 * Cloud Provider Zustand Store
 * Phase 3: State Management Migration - Centralized State Implementation
 * 
 * Professional Zustand store with localStorage persistence for cloud provider selection
 * Features: Type safety, persistence, validation, and extensibility
 */

import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { 
  type CloudProvider, 
  type CloudProviderConfig,
  DEFAULT_CLOUD_PROVIDER,
  CLOUD_PROVIDERS,
  isValidCloudProvider 
} from '@/types/cloudProvider'

interface CloudProviderState {
  // Core State
  provider: CloudProvider
  lastChanged: number
  
  // Actions
  setProvider: (provider: CloudProvider) => void
  resetProvider: () => void
  
  // Computed Values
  getProviderConfig: () => CloudProviderConfig
  getProviderName: () => string
  getProviderDisplayName: () => string
  
  // Validation
  isValidProvider: (provider: string) => provider is CloudProvider
  
  // Utilities
  getProviderColor: () => string
  getProviderBgColor: () => string
}

export const useCloudProviderStore = create<CloudProviderState>()(
  persist(
    (set, get) => ({
      // Initial State
      provider: DEFAULT_CLOUD_PROVIDER,
      lastChanged: Date.now(),
      
      // Core Actions
      setProvider: (provider: CloudProvider) => {
        // Validate provider before setting
        if (!isValidCloudProvider(provider)) {
          console.warn(`Invalid cloud provider: ${provider}. Falling back to default.`)
          return
        }
        
        set({ 
          provider,
          lastChanged: Date.now()
        })
      },
      
      resetProvider: () => {
        set({ 
          provider: DEFAULT_CLOUD_PROVIDER,
          lastChanged: Date.now()
        })
      },
      
      // Computed Values
      getProviderConfig: () => {
        const currentProvider = get().provider
        return CLOUD_PROVIDERS[currentProvider]
      },
      
      getProviderName: () => {
        const currentProvider = get().provider
        return CLOUD_PROVIDERS[currentProvider].name
      },
      
      getProviderDisplayName: () => {
        const currentProvider = get().provider
        return CLOUD_PROVIDERS[currentProvider].displayName
      },
      
      // Validation
      isValidProvider: isValidCloudProvider,
      
      // UI Utilities
      getProviderColor: () => {
        const currentProvider = get().provider
        return CLOUD_PROVIDERS[currentProvider].color
      },
      
      getProviderBgColor: () => {
        const currentProvider = get().provider
        return CLOUD_PROVIDERS[currentProvider].bgColor
      }
    }),
    {
      name: 'openconsole-cloud-provider', // localStorage key
      version: 1, // Schema version for future migrations
      
      // Only persist essential state, not computed functions
      partialize: (state) => ({ 
        provider: state.provider,
        lastChanged: state.lastChanged 
      }),
      
      // Migration function for schema changes
      migrate: (persistedState: any, version: number) => {
        if (version === 0) {
          // Handle migration from version 0 to 1
          return { 
            ...persistedState, 
            lastChanged: Date.now() 
          }
        }
        return persistedState as Partial<CloudProviderState>
      },
      
      // Validate persisted data
      onRehydrateStorage: () => (state) => {
        if (state && !isValidCloudProvider(state.provider)) {
          console.warn('Invalid persisted provider, resetting to default')
          state.provider = DEFAULT_CLOUD_PROVIDER
          state.lastChanged = Date.now()
        }
      }
    }
  )
)

/**
 * Selector hooks for optimized component subscriptions
 * These prevent unnecessary re-renders by subscribing only to specific state slices
 */

// Subscribe only to provider changes
export const useCurrentProvider = () => 
  useCloudProviderStore((state) => state.provider)

// Subscribe only to provider configuration
export const useProviderConfig = () => 
  useCloudProviderStore((state) => state.getProviderConfig())

// Subscribe only to provider actions
export const useProviderActions = () => 
  useCloudProviderStore((state) => ({
    setProvider: state.setProvider,
    resetProvider: state.resetProvider
  }))

// Subscribe to provider UI utilities
export const useProviderDisplay = () => 
  useCloudProviderStore((state) => ({
    name: state.getProviderName(),
    displayName: state.getProviderDisplayName(),
    color: state.getProviderColor(),
    bgColor: state.getProviderBgColor()
  }))